/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2026.
 *
 * Production-safe per-session override for the DiBot React widget.
 *
 * Lets internal users force-enable (or force-disable) the widget without
 * touching CMC storeconf. Safe to ship to production:
 * - Override never auto-applies; requires a deliberate URL action.
 * - Override is session-scoped (cleared on tab close).
 * - Worst case for an external user who lands on a ?dibotReact=1 link:
 *   they see the new widget for one browser session, no auth or business
 *   logic is bypassed.
 *
 * Usage:
 * - Append `?dibotReact=1` to any storefront URL → widget forced ON for
 *   the rest of the session.
 * - Append `?dibotReact=0` → clears the override; falls back to storeconf.
 *
 * Returns:
 *   true  → force enable
 *   false → force disable
 *   null  → no override; caller should consult storeconf
 */
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'dibot_react_force';
const QUERY_PARAM = 'dibotReact';

export const useDibotReactOverride = (): boolean | null => {
	const params = useSearchParams();
	const [override, setOverride] = useState<boolean | null>(null);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const param = params?.get(QUERY_PARAM);
		if (param === '1') {
			window.sessionStorage.setItem(STORAGE_KEY, '1');
			setOverride(true);
			return;
		}
		if (param === '0') {
			window.sessionStorage.removeItem(STORAGE_KEY);
			setOverride(false);
			return;
		}
		setOverride(window.sessionStorage.getItem(STORAGE_KEY) === '1' ? true : null);
	}, [params]);

	return override;
};
